import { createContext, useContext, useEffect} from 'react';
import graph from '@utils/graph';
import IProvider from '@models/IProvider';
import db from '@utils/db';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { msalConfig, silentRequest } from '@libs/teams-auth/auth-service/authConfig';
import { PublicClientApplication } from '@azure/msal-browser';
import moment from 'moment/moment';
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import OnScreenDebugger from '@utils/OnScreenDebuuger';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { MsAuthPlugin } from "@recognizebv/capacitor-plugin-msauth";

import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useAuthBase } from './useAuthBase';
import { useStore } from '@store/appStore';
import { tokenExpiresSoon } from '@utils/tokenRefresh';

// The Azure AD-specific claims this app reads out of the id/access token - jwt-decode's own
// JwtPayload only has the standard JWT claims, not these.
interface AzureJwtPayload extends JwtPayload {
  oid: string;
  upn: string;
  given_name: string;
  family_name: string;
  tid: string;
  name: string;
}

type IAuthProvider = {
  user: any;
  error: any;
  login: () => any;
  logout: () => any;
  getProfilePic: (res: string) => Promise<string>;
  getProfile: () => Promise<any>;
  groups: string[];
  getId: () => any;
  getType: () => any;
  getStudentId: () => any;
  loginMitId: (url) => any;
  getTokenTeams: (string) => any;
  // Web only - mobile reloads to sign in again when the app comes back instead (see appStateChange).
  signInAgain?: () => Promise<void>;
};

const AuthContext = createContext({
  user: null, // User account from MSAL
  error: null, // Error from MSAL
  login: () => { }, //login function
  logout: () => { }, //logout function
  getProfilePic: null, // getting profile picture function
  getProfile: null, //get user profile function
  groups: [], //user groups
} as IAuthProvider);

const msalInstance = new PublicClientApplication(msalConfig);

// useMsal()/useIsAuthenticated() below need MsalProvider as an ancestor, so it
// has to wrap this component from the outside rather than be rendered inside it.
export function MobileAuthProvider({ children }: IProvider) {
  return (
    <MsalProvider instance={msalInstance}>
      <MobileAuthProviderInner>{children}</MobileAuthProviderInner>
    </MsalProvider>
  );
}

function MobileAuthProviderInner({ children }: IProvider) {
  const mobile = true;
  const isDemo = false;
  const isMultiLogin = false
  const teams = false;
  const isLoggedIn = useIsAuthenticated();
  const { instance } = useMsal();
  const {
    demo, setDemo, error, seterror, router,
    authenticator, setAuthenticator, loggingIn, setlogginIn, setPersistentStore,
    client, setClientData, setSettings, settings, setWhoIsLoggedIn, setTutorial, setGroups, groups,
    token, settoken, setMobileLogin, mobileLogin, setMenuOpen, setSafeArea, reset, setTenantId,
    systemSettings, ids, setIds, user, setUser,
    initialRegistrationTypes, setInitialRegistrationTypes, hideAbsenceMenu, setHideAbsenceMenu,
    setCaseWorkerId, setAlerts, caseWorkerEmail, setCaseWorkerEmail, schoolName,
    setSchoolName, setSelectedCaseworker, wondeTags, setWondeTags, multilogin, setMultiLogin,
    setOtherUser, otherUser, whoIsLoggedIn, setSchools, setGuardianStudents, guardianStudents,
    filterDate, schools, setNotificationCounter,
    selectedSemester, kohortsAvailable, setKohortsAvailable, setPopupLogin,
    setActive, active, apiUrl, setApiUrl,
    getId, getStudentId, getType, getCookie, setCookie, delete_cookie, loginMitId, getTokenTeams,
    getDemoData, assignGroups,
    awaitSettings, getMitidToken, assignGroupsMitId,
  } = useAuthBase({ mobile, isDemo, teams, isMultiLogin, getProfile, instance });
  const setStatusBarStyleDark = async () => {
    try {
      await StatusBar.setStyle({ style: Style.Default });

    } catch (error) {

    }
  };

  useEffect(() => {
    setStatusBarStyleDark()
    App.addListener('appStateChange', ({ isActive }) => {
      //db.Log(isActive)
      //setActive(isActive)

      // Mobile's token lives only in memory (user.access_token, from MsAuthPlugin) and nothing
      // renews it, so an app resumed after an hour+ in the background kept sending an expired one
      // (Graph 401s, webservice 403s). The app can't be "closed and reopened" like a browser tab,
      // so reload instead: with localStorage's login flag still set, startup goes straight back
      // through MsAuthPlugin.login, which signs in silently first. Storage is deliberately left
      // alone - clearing localStorage would drop that flag and land on the start screen instead.
      // Read from the store, not `user` - this listener is registered once, so `user` is stale.
      const accessToken = useStore.getState().user?.access_token;
      if (isActive && accessToken && tokenExpiresSoon(accessToken)) {
        window.location.reload();
      }
    });

    SafeArea.getSafeAreaInsets()
      .then(({ insets }) => {
        if (Capacitor.getPlatform().toLowerCase() === 'ios') setSafeArea(insets);
      })
      .catch((error) => { });
    // Handle the 'appUrlOpen' event and call `handleRedirectCallback`
    CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (url.includes('#access_token')) {
        let code = url.split('#access_token=');
        //let idcode = url.split('id_token=');
        if (code.length > 1) {
          code = code[1].split('&token_type');
          //idcode  = idcode[1].split('&session_state');        
          if (Capacitor.getPlatform().toLowerCase() === 'ios') await Browser.close();
          await getToken(code[0], true);
        }
      } else if (url.includes('#error')) {
        setDemo(false);
        setMenuOpen(false);
        localStorage.clear();
        sessionStorage.clear();
        reset();
        if (Capacitor.getPlatform().toLowerCase() === 'ios') await Browser.close();
      }
    });
  }, []);

  // Restores the settings-loading gate that existed before the desktop/
  // mobile split (was `if (mobile) {...}` in the shared settings useEffect) -
  // without it, settings never gets fetched here at all, so mobileLogin was
  // being set (and downstream flows like getDemoData reading `settings`)
  // before any settings had loaded, crashing on null.settings access.
  useEffect(() => {
    if (settings) {
      if (settings.ENABLE_PRIVACY_SETTING === 'false') {
        setPersistentStore((draft) => {
          draft.privacyEnabled = true;
        });
      }
      if (mobileLogin === null) {
        if (localStorage.getItem('login') === 'true') setMobileLogin(3);
        else setMobileLogin(1);
      }
      return;
    }
    awaitSettings();
  }, [settings]);

  async function getToken(code, oldLogin?) {
    if (oldLogin) {
      let decoded = jwtDecode<AzureJwtPayload>(code)
      let user = {
        localAccountId: decoded.oid,
        username: decoded.upn,
        given_name: decoded.given_name,
        family_name: decoded.family_name,
        tenantId: decoded.tid,
        access_token: code,
        name: decoded.name
      }
      setUser(user);
    }
    else {
      let decoded = jwtDecode<AzureJwtPayload>(code.accessToken)
      let user = {
        localAccountId: decoded.oid,
        username: decoded.upn,
        given_name: decoded.given_name,
        family_name: decoded.family_name,
        tenantId: decoded.tid,
        access_token: code.accessToken,
        name: decoded.name
      }
      setUser(user);
    }

  }

  useEffect(() => {
    if (client && user && user.access_token && apiUrl && mobileLogin === 3) {
      assignGroups();
    }
  }, [client, apiUrl, user]);

  async function loginMobile() {
    try {
      let result = await MsAuthPlugin.login({
        clientId: '3741153e-2490-463c-aff7-5283fb1bd1b4',
        scopes: ['user.read'],
        keyHash: '/Vz+tZv4wylCiuI8jDyQYbSJqKI=',
        authorityUrl: 'https://login.microsoftonline.com/organizations',
      });
      getToken(result);
    } catch (error) {

      OnScreenDebugger.instance.log('browser.open');
      let redirecturi = Capacitor.getPlatform().toLowerCase() === 'android' ? 'com.attender.app%3A%2F%2Fcallback' : 'attenderapp://callback';
      await Browser.open({
        url:
          'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=3741153e-2490-463c-aff7-5283fb1bd1b4&scope=openid%20profile%20offline_access%20user.read&redirect_uri=' +
          redirecturi +
          '&response_mode=fragment&response_type=token&nonce=123',
      }).catch(error => OnScreenDebugger.instance.log(error));

    }
  }

  useEffect(() => {
    if (mobileLogin === 3) {
      loginMobile();
    } else if (mobileLogin === 2 && demo !== true) {
      getDemoData();
    }
  }, [mobileLogin]);

  async function getProfilePic(resolution: string = '240x240'): Promise<string> {
    if (settings.DEMO === 'true' || settings.DEMO === true) {
      return null;
    }
    return graph.getPhoto(user.access_token, resolution);
  }

  async function getProfile(): Promise<any> {
    return graph.getSelf(user.access_token);
  }

  async function logout() {
    setUser(null);
    delete_cookie('msal_username');
    sessionStorage.clear();
    setMenuOpen(false);
    localStorage.clear();
    setlogginIn(false);
    reset();
    await router.push('/');
    if (mobileLogin === 3) {
      OnScreenDebugger.instance.log('browser.open');
      try {
        await MsAuthPlugin.logout({
          clientId: '3741153e-2490-463c-aff7-5283fb1bd1b4',
          keyHash: '/Vz+tZv4wylCiuI8jDyQYbSJqKI=',
          authorityUrl: 'https://login.microsoftonline.com/organizations',
        });
      }
      catch (e) {
        let redirecturi =
          Capacitor.getPlatform().toLowerCase() === 'android' ? 'com.attender.app%3A%2F%2Fcallback' : 'attenderapp%3A%2F%2Fcallback';
        await Browser.open({
          url:
            'https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=' +
            redirecturi +
            '&logout_hint=' +
            user.loginHint,
        });
        let platform = Capacitor.getPlatform().toLowerCase();
        setTimeout(() => {
          if (platform === 'android') {
            Browser.open({ url: 'com.attender.app://callback' });
          } else {
            Browser.close();
          }

          setMobileLogin(1);
        }, 4000);
      }
      setMobileLogin(1);
    } else {
      setMobileLogin(1);
      setDemo(false);
    }
  }

  async function login() {
    try {
      // Call the MSAL auth helper to login
      if (!teams) {
        console.log(isLoggedIn, sessionStorage.getItem('loggingIn'));
        if (sessionStorage.getItem('loggingIn') !== '1' && !isLoggedIn) {
          console.log('tttt');
          sessionStorage.setItem('loggingIn', '1');
          let username = getCookie('msal_username');
          console.log(username);
          if (username && username.length > 0)
            await instance.loginRedirect({
              ...silentRequest,
              loginHint: username,
            });
          else await instance.loginRedirect({ ...silentRequest });
        }
      } else await authenticator.login();
    } catch (err) {
      //OnScreenDebugger.instance.log('AUTH: MSAL ERROR' + err);

      seterror(err.toString());
    }
  }


  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        login,
        logout,
        getProfilePic,
        groups,
        getProfile,
        getId,
        getType,
        getStudentId,
        loginMitId,
        getTokenTeams,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthentication = () => useContext(AuthContext);
